import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Lock, KeyRound, Mail, ArrowLeft, Loader2 } from "lucide-react";
import api from "../api";
import { useApp } from "../AppContext";
import { toast } from "sonner";
import DevBranding from "../components/DevBranding";

/**
 * Two-step admin password reset.
 *
 * Step "request": admin proves identity by either
 *   (a) submitting their bcrypt-verified recovery code (returns a token immediately), or
 *   (b) requesting a magic link to ADMIN_RECOVERY_EMAIL (deliveries via Resend).
 *
 * Step "set": with a valid 1-hour reset token (either typed in from the
 * URL `?token=...` or returned in step (a)), the admin types a new password
 * and/or PIN. Backend stores the new bcrypt hashes in MongoDB —
 * ZERO redeploy required.
 */
const AdminPasswordReset = () => {
    const { t } = useApp();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const tokenFromUrl = params.get("token") || "";

    const [step, setStep] = useState(tokenFromUrl ? "set" : "request");
    const [method, setMethod] = useState("code"); // "code" | "email"
    const [code, setCode] = useState("");
    const [email, setEmail] = useState("");
    const [token, setToken] = useState(tokenFromUrl);
    const [newPassword, setNewPassword] = useState("");
    const [newPin, setNewPin] = useState("");
    const [loading, setLoading] = useState(false);

    // If the token in the URL is valid we drop the user straight into the
    // "set new password" form. The token is verified server-side on submit
    // — we just check presence here.
    useEffect(() => {
        if (tokenFromUrl) setStep("set");
    }, [tokenFromUrl]);

    const submitRequest = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const body = method === "code"
                ? { method: "code", code: code.trim() }
                : { method: "email", email: email.trim() };
            const res = await api.post("/admin/forgot-password", body);
            if (res.data?.method === "code" && res.data?.reset_token) {
                setToken(res.data.reset_token);
                setStep("set");
                toast.success(t.admin.resetCodeAccepted);
            } else {
                // Email path — no token in response by design.
                toast.success(
                    t.admin.resetEmailSent.replace(
                        "{minutes}",
                        String(res.data?.expires_in_minutes || 60)
                    )
                );
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || t.common.error);
        } finally {
            setLoading(false);
        }
    };

    const submitNewPassword = async (e) => {
        e.preventDefault();
        if (!newPassword && !newPin) {
            toast.error("Provide a new password and/or PIN");
            return;
        }
        setLoading(true);
        try {
            await api.post("/admin/reset-password", {
                token,
                new_password: newPassword || undefined,
                new_pin: newPin || undefined,
            });
            toast.success(t.admin.resetSuccess);
            setTimeout(() => navigate("/admin/login"), 1500);
        } catch (err) {
            const msg = err?.response?.data?.detail || t.common.error;
            // 401 = expired/invalid token. Drop user back to step 1.
            if (err?.response?.status === 401) {
                toast.error(t.admin.resetTokenExpired);
                setStep("request");
                setToken("");
            } else {
                toast.error(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            data-testid="admin-reset-page"
            className="min-h-screen flex flex-col items-center justify-center bg-earth-100 grain p-6"
        >
            <div className="w-full max-w-md bg-white border border-earth-200 rounded-[28px] shadow-xl p-10">
                <Link
                    to="/admin/login"
                    className="text-xs uppercase tracking-wider text-earth-500 hover:text-terracotta-600 inline-flex items-center gap-1"
                    data-testid="reset-back-to-login-link"
                >
                    <ArrowLeft className="w-3 h-3" />
                    {t.admin.resetBackToLogin}
                </Link>
                <div className="mt-6 mb-7">
                    <div className="w-12 h-12 rounded-full bg-terracotta-500/10 flex items-center justify-center mb-3">
                        <Lock className="w-5 h-5 text-terracotta-600" />
                    </div>
                    <h1 className="font-serif text-2xl font-bold text-earth-900">
                        {t.admin.resetTitle}
                    </h1>
                    <p className="text-xs text-earth-600 mt-1">
                        {t.admin.resetSubtitle}
                    </p>
                </div>

                {step === "request" && (
                    <form onSubmit={submitRequest} className="space-y-5" data-testid="reset-request-form">
                        <div className="grid grid-cols-2 gap-2 bg-earth-100 rounded-full p-1">
                            <button
                                type="button"
                                data-testid="reset-method-code-btn"
                                onClick={() => setMethod("code")}
                                className={`inline-flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                                    method === "code" ? "bg-white text-earth-900 shadow-sm" : "text-earth-600"
                                }`}
                            >
                                <KeyRound className="w-3.5 h-3.5" />
                                {t.admin.resetMethodCode}
                            </button>
                            <button
                                type="button"
                                data-testid="reset-method-email-btn"
                                onClick={() => setMethod("email")}
                                className={`inline-flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                                    method === "email" ? "bg-white text-earth-900 shadow-sm" : "text-earth-600"
                                }`}
                            >
                                <Mail className="w-3.5 h-3.5" />
                                {t.admin.resetMethodEmail}
                            </button>
                        </div>

                        {method === "code" ? (
                            <div>
                                <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
                                    {t.admin.resetMethodCode}
                                </label>
                                <input
                                    data-testid="reset-code-input"
                                    type="text"
                                    autoComplete="off"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    placeholder={t.admin.resetCodePlaceholder}
                                    className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500 font-mono uppercase tracking-wider text-center"
                                />
                            </div>
                        ) : (
                            <div>
                                <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
                                    {t.admin.resetMethodEmail}
                                </label>
                                <input
                                    data-testid="reset-email-input"
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={t.admin.resetEmailPlaceholder}
                                    className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500"
                                />
                            </div>
                        )}

                        <button
                            type="submit"
                            data-testid="reset-request-submit-btn"
                            disabled={loading || (method === "code" ? !code : !email)}
                            className="btn-primary w-full"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : method === "code" ? (
                                t.admin.resetSendCode
                            ) : (
                                t.admin.resetSendEmail
                            )}
                        </button>
                    </form>
                )}

                {step === "set" && (
                    <form onSubmit={submitNewPassword} className="space-y-5" data-testid="reset-set-form">
                        <div>
                            <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
                                {t.admin.resetNewPassword}
                            </label>
                            <input
                                data-testid="reset-new-password-input"
                                type="password"
                                autoComplete="new-password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                minLength={10}
                                className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500"
                            />
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
                                {t.admin.resetNewPin}
                            </label>
                            <input
                                data-testid="reset-new-pin-input"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={6}
                                value={newPin}
                                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                                className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500 text-2xl tracking-[0.4em] text-center"
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="reset-submit-btn"
                            disabled={loading || (!newPassword && !newPin)}
                            className="btn-primary w-full"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                t.admin.resetSubmit
                            )}
                        </button>
                    </form>
                )}
            </div>
            <div className="mt-8">
                <DevBranding variant="footer" />
            </div>
        </div>
    );
};

export default AdminPasswordReset;
